<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { getOptimizedImageUrl } from '../utils/image'
import { useCartStore } from '../stores/cartStore'
import { useSettingsStore } from '../stores/settingsStore'
import { useWishlistStore } from '../stores/wishlistStore'
import { useCompareStore } from '../stores/compareStore'
import { useAuthStore } from '../stores/authStore'
import { useTrackingStore } from '../stores/trackingStore'
import { useToast } from '../composables/useToast'
import { usePromotionsStore } from '../stores/promotionsStore'
import { saveClickedProductContext } from '../utils/scrollRestoration'
import { getCardIconPath } from '../utils/cardIcons'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  priority: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()
const wishlistStore = useWishlistStore()
const compareStore = useCompareStore()
const authStore = useAuthStore()
const trackingStore = useTrackingStore()
const promotionsStore = usePromotionsStore()
const { showToast } = useToast()

// Promotions & Pricing
const promoInfo = computed(() => {
  return promotionsStore.getProductPromoInfo(props.product.id)
})

const effectivePrice = computed(() => {
  if (props.product.promo_price !== undefined && props.product.promo_price !== null && Number(props.product.promo_price) > 0) {
    return Number(props.product.promo_price)
  }
  if (promoInfo.value && promoInfo.value.promo_price !== undefined && promoInfo.value.promo_price !== null) {
    return Number(promoInfo.value.promo_price)
  }
  return Number(props.product.price || 0)
})

const effectiveOriginalPrice = computed(() => {
  if (promoInfo.value && promoInfo.value.original_price > effectivePrice.value) {
    return Number(promoInfo.value.original_price)
  }
  return Number(props.product.originalPrice || props.product.original_price || 0)
})

const savingsAmount = computed(() => {
  const orig = effectiveOriginalPrice.value
  const curr = effectivePrice.value
  if (orig > curr && orig > 0) {
    return orig - curr
  }
  return 0
})

const discountPercent = computed(() => {
  const orig = effectiveOriginalPrice.value
  const curr = effectivePrice.value
  if (orig > curr && orig > 0) {
    return Math.round(((orig - curr) / orig) * 100)
  }
  return 0
})

// Model SKU
const modelSku = computed(() => {
  if (props.product.sku && props.product.sku.trim()) {
    return props.product.sku.trim()
  }
  return 'MS-SERIES'
})

// Card Customization & Feature Engine
const cardFeatures = computed(() => {
  const cf = props.product.card_features
  if (!cf) return null
  if (typeof cf === 'string') {
    try { return JSON.parse(cf) } catch { return null }
  }
  return cf
})

const isCardFeaturesActive = computed(() => {
  return cardFeatures.value && cardFeatures.value.enabled !== false
})

// Top-left status badge (Custom / Preset / Fallback automatic)
const topStatusBadge = computed(() => {
  if (isCardFeaturesActive.value && cardFeatures.value?.top_badge?.enabled) {
    const tb = cardFeatures.value.top_badge
    return {
      type: tb.type || 'custom',
      label: tb.label || 'BEST SELLER',
      color: tb.color || 'emerald',
      icon: tb.icon || 'crown'
    }
  }
  if (props.product.badge_free_shipping) {
    return { type: 'free_shipping_all', label: 'ส่งฟรีทั่วประเทศ', color: 'emerald', icon: 'truck' }
  }
  if (props.product.free_shipping_bkk) {
    return { type: 'free_shipping_bkk', label: 'ส่งฟรี กทม', color: 'emerald', icon: 'truck' }
  }
  const sku = modelSku.value.toUpperCase()
  if (sku.includes('NH140') || props.product.badge_recommended || props.product.badge_bestseller) {
    return { type: 'bestseller', label: 'BEST SELLER', color: 'emerald', icon: 'crown' }
  }
  if (sku.includes('NH210')) {
    return { type: 'popular', label: 'POPULAR', color: 'emerald', icon: 'flame' }
  }
  if (sku.includes('S003') || props.product.badge_new) {
    return { type: 'new', label: 'NEW', color: 'emerald', icon: 'sparkles' }
  }
  // Deterministic cycle across other products
  const code = String(props.product.id || sku).charCodeAt(0) || 0
  if (code % 3 === 0) return { type: 'bestseller', label: 'BEST SELLER', color: 'emerald', icon: 'crown' }
  if (code % 3 === 1) return { type: 'popular', label: 'POPULAR', color: 'emerald', icon: 'flame' }
  return { type: 'new', label: 'NEW', color: 'emerald', icon: 'sparkles' }
})

const getTopBadgeColorClass = (color) => {
  switch (color) {
    case 'rose':
    case 'red':
      return 'bg-rose-700/95 border-rose-400/30 text-white'
    case 'amber':
    case 'orange':
      return 'bg-amber-600/95 border-amber-300/30 text-white'
    case 'blue':
      return 'bg-blue-700/95 border-blue-400/30 text-white'
    case 'purple':
      return 'bg-purple-700/95 border-purple-400/30 text-white'
    case 'slate':
    case 'dark':
      return 'bg-slate-900/95 border-slate-700 text-white'
    case 'emerald':
    default:
      return 'bg-[#115e42]/95 border-white/15 text-white'
  }
}

// Left Stack Feature Badges
const leftStackFeatures = computed(() => {
  if (!isCardFeaturesActive.value) return []
  if (cardFeatures.value.show_stack === false) return []
  const stack = cardFeatures.value.stack
  if (!Array.isArray(stack)) return []
  return stack.filter(item => item && (item.text || item.icon)).slice(0, 4)
})

// Right Hero Badge
const rightHeroBadge = computed(() => {
  if (!isCardFeaturesActive.value) return null
  if (cardFeatures.value.show_badge === false) return null
  const b = cardFeatures.value.badge
  if (!b || (!b.text1 && !b.text2)) return null
  return {
    icon: b.icon || 'shield',
    text1: b.text1 || '',
    text2: b.text2 || '',
    color: b.color || 'emerald'
  }
})

const getRightBadgeColorClass = (color) => {
  switch (color) {
    case 'blue':
      return 'bg-blue-700 text-white border-blue-500 shadow-blue-900/30'
    case 'slate':
    case 'dark':
      return 'bg-slate-900 text-white border-slate-700 shadow-slate-950/40'
    case 'red':
    case 'rose':
      return 'bg-rose-700 text-white border-rose-500 shadow-rose-900/30'
    case 'amber':
      return 'bg-amber-600 text-white border-amber-400 shadow-amber-900/30'
    case 'emerald':
    default:
      return 'bg-[#227b58] text-white border-[#319c72] shadow-emerald-950/30'
  }
}

// Structured 2-Line Title (Line 1: Product Type, Line 2: Model SKU)
const formattedTitleParts = computed(() => {
  let raw = props.product.title || props.product.name || ''
  raw = raw.replace(/\s*\(\s*ขนาด[^)]*\)/gi, '').replace(/\s*ไม่มีพื้นภายใน\s*$/gi, '').trim()

  const modelMatch = raw.match(/^(.*?)\s*(รุ่น\s+[A-Za-z0-9-_]+.*)$/i)
  if (modelMatch && modelMatch[1].trim()) {
    return {
      main: modelMatch[1].trim(),
      model: modelMatch[2].trim()
    }
  }
  return {
    main: raw || (props.product.category_name || 'บ้านเก็บของพลาสติก HDPE'),
    model: `รุ่น ${modelSku.value}`
  }
})

// Full-width natural product title
const displayTitle = computed(() => {
  let raw = props.product.title || props.product.name || ''
  raw = raw.replace(/\s*\(\s*ขนาด[^)]*\)/gi, '').replace(/\s*ไม่มีพื้นภายใน\s*$/gi, '').trim()
  if (raw) return raw
  return `${formattedTitleParts.value.main} ${formattedTitleParts.value.model}`.trim()
})

// Descriptive Subtitle Line under Title on the Image Overlay
const heroTagline = computed(() => {
  if (isCardFeaturesActive.value && cardFeatures.value.custom_tagline && cardFeatures.value.custom_tagline.trim()) {
    return cardFeatures.value.custom_tagline.trim()
  }
  const rawDim = props.product.size || props.product.dimensions
  if (rawDim && rawDim.trim()) {
    const cleanDim = rawDim.trim().replace(/\s*x\s*/gi, ' × ').replace(/เซนติเมตร/g, 'ซม.').replace(/เมตร/g, 'ม.')
    return `ขนาด ${cleanDim} • แข็งแรง ทนทาน ใช้งานได้ยาวนาน`
  }
  const sku = modelSku.value.toUpperCase()
  if (sku.includes('NH210')) return 'พื้นที่จัดเก็บขนาดใหญ่ ตอบโจทย์ทุกการใช้งาน'
  if (sku.includes('S003')) return 'ดีไซน์ทันสมัย จัดเก็บได้หลากหลาย'
  return 'แข็งแรง ทนทาน ใช้งานได้ยาวนาน'
})

// Gallery Images Management (Interactive 4-thumbnail strip)
const allGalleryImages = computed(() => {
  const main = props.product.image || props.product.image_url
  let extra = []
  if (Array.isArray(props.product.images)) {
    extra = props.product.images
  } else if (typeof props.product.images === 'string') {
    try {
      const parsed = JSON.parse(props.product.images)
      if (Array.isArray(parsed)) extra = parsed
    } catch {}
  }
  const combined = [main, ...extra].filter(Boolean)
  const unique = [...new Set(combined)]

  if (unique.length >= 4) {
    return unique.map((src, idx) => ({ src, zoomClass: 'object-cover object-center', key: idx }))
  }
  // If product has fewer than 4 gallery images, fill remaining slots with detail crop perspectives
  const fallbackSrc = unique[0] || '/placeholder.jpg'
  const crops = [
    'object-cover object-center',
    'object-cover object-top scale-125',
    'object-cover object-bottom scale-125',
    'object-cover object-right-top scale-150'
  ]
  const result = []
  for (let i = 0; i < 4; i++) {
    if (unique[i]) {
      result.push({ src: unique[i], zoomClass: 'object-cover object-center', key: i })
    } else {
      result.push({ src: fallbackSrc, zoomClass: crops[i % crops.length], key: i })
    }
  }
  return result
})

const activeImageIndex = ref(0)
const thumbnailOffset = ref(0)

watch(() => props.product.id, () => {
  activeImageIndex.value = 0
  thumbnailOffset.value = 0
})

const visibleThumbnails = computed(() => {
  const list = allGalleryImages.value
  const out = []
  for (let i = 0; i < 4; i++) {
    const idx = (thumbnailOffset.value + i) % list.length
    out.push({ ...list[idx], originalIndex: idx })
  }
  return out
})

const activeHeroImage = computed(() => {
  const list = allGalleryImages.value
  return list[activeImageIndex.value] || list[0]
})

const selectThumbnail = (e, idx) => {
  e.preventDefault()
  e.stopPropagation()
  activeImageIndex.value = idx
}

const prevGalleryImage = (e) => {
  e.preventDefault()
  e.stopPropagation()
  const total = allGalleryImages.value.length
  activeImageIndex.value = (activeImageIndex.value - 1 + total) % total
  if (total > 4) {
    thumbnailOffset.value = (thumbnailOffset.value - 1 + total) % total
  }
}

const nextGalleryImage = (e) => {
  e.preventDefault()
  e.stopPropagation()
  const total = allGalleryImages.value.length
  activeImageIndex.value = (activeImageIndex.value + 1) % total
  if (total > 4) {
    thumbnailOffset.value = (thumbnailOffset.value + 1) % total
  }
}

// 4-Column Bottom Icon Specification Bar
const fourIconSpecs = computed(() => {
  const cat = (props.product.category_name || '').toLowerCase()
  const name = (props.product.title || props.product.name || '').toLowerCase()

  let matLine1 = 'วัสดุ HDPE'
  if (cat.includes('เมทัล') || cat.includes('โลหะ') || name.includes('เมทัล')) {
    matLine1 = 'เมทัลชีท'
  } else if (cat.includes('pp') || name.includes(' pp ')) {
    matLine1 = 'พลาสติก PP'
  } else if (cat.includes('โรงเรือน')) {
    matLine1 = 'โครงกัลวาไนซ์'
  }

  return [
    { icon: 'layers', line1: matLine1, line2: 'คุณภาพสูง' },
    { icon: 'droplet', line1: 'กันน้ำ', line2: '100%' },
    { icon: 'sun', line1: 'ทนแดด', line2: 'UV 100%' },
    { icon: 'tools', line1: 'ประกอบง่าย', line2: 'ติดตั้งสะดวก' }
  ]
})

// Active Bottom Specification Bar: Custom Bottom Bar (from card_features) or Category Default
const bottomBarSpecs = computed(() => {
  if (isCardFeaturesActive.value && cardFeatures.value.show_bottom_bar !== false) {
    const list = cardFeatures.value.bottom_bar
    if (Array.isArray(list) && list.length > 0) {
      const valid = list.filter(item => item && (item.title || item.icon))
      if (valid.length > 0) {
        return valid.map(item => ({
          icon: item.icon || 'check',
          line1: item.title || '',
          line2: item.subtitle || ''
        }))
      }
    }
  }
  return fourIconSpecs.value
})

const showBottomBar = computed(() => {
  if (isCardFeaturesActive.value && cardFeatures.value.show_bottom_bar === false) {
    return false
  }
  return bottomBarSpecs.value.length > 0
})

// Store Actions
const handlePrimaryAction = async (e, product) => {
  e.preventDefault()
  e.stopPropagation()
  if (settingsStore.isOnlineShoppingEnabled && !product.is_out_of_stock) {
    try {
      await cartStore.addToCart(product, 1)
      showToast(`เพิ่ม ${product.title || product.name} ลงในตะกร้าแล้ว`, 'success')
      trackingStore.trackEvent({
        type: 'add_to_cart',
        productId: product.id,
        productName: product.title || product.name,
        price: product.price,
        quantity: 1,
        source: 'product_card'
      })
    } catch {
      showToast('ไม่สามารถเพิ่มลงตะกร้าได้', 'error')
    }
  } else {
    onCardClick()
    router.push('/products/' + (product.slug || product.id))
  }
}

const toggleWishlistItem = async (e, product) => {
  e.preventDefault()
  e.stopPropagation()
  if (!authStore.isAuthenticated) {
    showToast('กรุณาเข้าสู่ระบบก่อนเพิ่มรายการโปรด', 'warning')
    return
  }
  const result = await wishlistStore.toggleWishlist(product)
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการโปรดแล้ว' : 'นำออกจากรายการโปรดแล้ว', result.isAdded ? 'success' : 'info')
    trackingStore.trackEvent({
      type: result.isAdded ? 'add_to_wishlist' : 'remove_from_wishlist',
      productId: product.id,
      productName: product.title || product.name,
      source: 'product_card'
    })
  }
}

const toggleCompareItem = (e, product) => {
  e.preventDefault()
  e.stopPropagation()
  const result = compareStore.toggleCompare(product)
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการเปรียบเทียบแล้ว' : 'นำออกจากรายการเปรียบเทียบแล้ว', result.isAdded ? 'success' : 'info')
  } else if (result.error === 'max_reached') {
    showToast('เปรียบเทียบได้สูงสุด 4 ชิ้น', 'warning')
  }
}

const formatPrice = (price) => {
  if (!price) return '0'
  return new Intl.NumberFormat('th-TH').format(price)
}

const onCardClick = () => {
  saveClickedProductContext(props.product.slug || props.product.id, cardRef.value)
}

// Viewport-aware lazy rendering for thumbnails (1 shared observer across all ProductCard instances)
const cardRef = ref(null)
const isNearViewport = ref(props.priority)

function getSharedCardObserver() {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null
  if (!window.__msProductCardObserver) {
    const callbacks = new WeakMap()
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const cb = callbacks.get(entry.target)
          if (cb) {
            cb()
            callbacks.delete(entry.target)
          }
          obs.unobserve(entry.target)
        }
      }
    }, { rootMargin: '350px 0px', threshold: 0.01 })
    window.__msProductCardObserver = { obs, callbacks }
  }
  return window.__msProductCardObserver
}

onMounted(() => {
  if (props.priority) {
    isNearViewport.value = true
    return
  }
  const shared = getSharedCardObserver()
  if (shared && cardRef.value) {
    shared.callbacks.set(cardRef.value, () => {
      isNearViewport.value = true
    })
    shared.obs.observe(cardRef.value)
  } else {
    isNearViewport.value = true
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined' && window.__msProductCardObserver && cardRef.value) {
    window.__msProductCardObserver.obs.unobserve(cardRef.value)
    window.__msProductCardObserver.callbacks.delete(cardRef.value)
  }
})

// Image load & error handling
const imgError = ref(false)

const onImgError = (event) => {
  const img = event.target
  if (!img || img.dataset.fallbackAttempted === 'true') {
    imgError.value = true
    return
  }
  const src = img.getAttribute('src') || img.src
  const cacheMatch = src.match(/\/uploads\/(?:(.+)\/)?cache\/([^/]+)-(\d+)\.([a-zA-Z0-9]+)$/)
  if (cacheMatch) {
    const subfolder = cacheMatch[1] || ''
    const baseName = cacheMatch[2]
    const ext = cacheMatch[4]
    const originalPath = subfolder
      ? `/uploads/${subfolder}/${baseName}.${ext}`
      : `/uploads/${baseName}.${ext}`
    img.dataset.fallbackAttempted = 'true'
    img.src = originalPath
  } else {
    imgError.value = true
  }
}
</script>

<template>
  <div 
    ref="cardRef"
    :data-product-slug="product.slug || ''"
    :data-product-id="product.id || ''"
    class="product-card-showcase group flex flex-col h-full rounded-[24px] bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 shadow-[0_8px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_20px_48px_rgba(11,83,59,0.16)] transition-all duration-500 overflow-hidden relative"
  >
    <!-- 1. HERO IMAGE WITH DARK GRADIENT OVERLAY & OVERLAID TYPOGRAPHY -->
    <div class="relative aspect-[1/1.02] w-full overflow-hidden bg-slate-200 dark:bg-slate-800">
      
      <!-- Fallback Icon -->
      <div v-if="imgError" class="absolute inset-0 z-[1] flex items-center justify-center bg-slate-100 dark:bg-slate-800">
        <svg class="w-12 h-12 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      </div>

      <!-- Main Hero Image -->
      <router-link 
        :to="'/products/' + (product.slug || product.id)" 
        @click="onCardClick"
        class="w-full h-full block relative z-[1]"
      >
        <img 
          :src="getOptimizedImageUrl(activeHeroImage.src, 600)" 
          :alt="product.title || product.name" 
          :loading="priority ? 'eager' : 'lazy'" 
          :fetchpriority="priority ? 'high' : 'auto'"
          decoding="async"
          width="600" 
          height="600" 
          class="w-full h-full transition-all duration-700 ease-out group-hover:scale-105"
          :class="activeHeroImage.zoomClass"
          @error="onImgError"
        >
      </router-link>

      <!-- TOP-LEFT STATUS PILL (Custom or Automatic - 50% compact footprint) -->
      <div v-if="topStatusBadge" class="absolute top-2 left-2 sm:top-2.5 sm:left-2.5 z-[4] pointer-events-none select-none">
        <span class="px-2 py-0.5 rounded-full backdrop-blur-md text-[9px] font-black tracking-wide uppercase shadow-sm flex items-center gap-1 border" :class="getTopBadgeColorClass(topStatusBadge.color)">
          <svg class="w-2.5 h-2.5 text-white shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" :d="getCardIconPath(topStatusBadge.icon)"/>
          </svg>
          <span>{{ topStatusBadge.label }}</span>
        </span>
      </div>

      <!-- LEFT STACK FEATURE BADGES (Interactive Slide-Out) -->
      <div v-if="leftStackFeatures.length > 0" class="absolute top-1/2 -translate-y-1/2 left-2.5 z-[4] flex flex-col gap-1.5 feat-stack">
        <div v-for="(feat, idx) in leftStackFeatures" :key="'cf-stack-' + idx" class="feat-badge" :style="`--i: ${idx}`">
          <div class="feat-badge-icon">
            <svg class="w-[15px] h-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" :d="getCardIconPath(feat.icon)"></path>
            </svg>
          </div>
          <span class="feat-badge-text">{{ feat.text }}</span>
        </div>
      </div>

      <!-- RIGHT HERO BADGE -->
      <div v-if="rightHeroBadge" class="absolute bottom-[94px] sm:bottom-[100px] right-3 z-[4] px-2.5 py-1.5 rounded-xl shadow-lg border flex items-center gap-1.5 transition-transform duration-300 group-hover:scale-105" :class="getRightBadgeColorClass(rightHeroBadge.color)">
        <svg v-if="rightHeroBadge.icon" class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" :d="getCardIconPath(rightHeroBadge.icon)"/>
        </svg>
        <div class="flex flex-col leading-[1.1]">
          <span class="text-[10px] font-black tracking-wide">{{ rightHeroBadge.text1 }}</span>
          <span v-if="rightHeroBadge.text2" class="text-[9px] font-normal opacity-90">{{ rightHeroBadge.text2 }}</span>
        </div>
      </div>

      <!-- TOP-RIGHT BADGES & WISHLIST BUTTON (50% compact footprint) -->
      <div class="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 z-[4] flex items-center gap-1">
        <span 
          v-if="discountPercent > 0" 
          class="px-1.5 py-0.5 rounded-full bg-rose-600 text-white text-[9px] font-black tracking-tight shadow-sm leading-none border border-white/20 select-none"
        >
          -{{ discountPercent }}%
        </span>
        <button
          type="button"
          @click="toggleWishlistItem($event, product)"
          class="w-6 h-6 sm:w-6.5 sm:h-6.5 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-md hover:bg-rose-50 dark:hover:bg-rose-950/60 shadow-sm border border-white/80 dark:border-white/15 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 group/wish touch-manipulation"
          :title="wishlistStore.isInWishlist(product.id) ? 'นำออกจากรายการโปรด' : 'เพิ่มในรายการโปรด'"
          :aria-label="wishlistStore.isInWishlist(product.id) ? 'นำออกจากรายการโปรด' : 'เพิ่มในรายการโปรด'"
        >
        <svg v-if="wishlistStore.isInWishlist(product.id)" class="w-3 h-3 text-rose-500 transition-transform duration-200 scale-110 shrink-0" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/>
        </svg>
        <svg v-else class="w-3 h-3 text-slate-600 dark:text-slate-300 group-hover/wish:text-rose-500 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/>
        </svg>
        </button>
      </div>

      <!-- BOTTOM GRADIENT OVERLAY WITH PRODUCT TITLE -->
      <router-link 
        :to="'/products/' + (product.slug || product.id)" 
        @click="onCardClick"
        class="absolute inset-x-0 bottom-0 z-[3] pt-16 pb-4 px-4 sm:px-5 bg-gradient-to-t from-black/85 via-black/55 to-transparent flex flex-col justify-end"
      >

        <!-- Product Title: Full-width, proportional font size, natural wrapping -->
        <h3 class="w-full text-white text-[13.5px] sm:text-[14.5px] md:text-[15px] font-bold leading-snug tracking-tight drop-shadow-sm line-clamp-2">
          {{ displayTitle }}
        </h3>

        <!-- Subtitle / Tagline -->
        <p class="w-full text-white/85 text-[11px] font-medium mt-0.5 truncate">
          {{ heroTagline }}
        </p>
      </router-link>

      <!-- Out of Stock Overlay -->
      <div v-if="product.is_out_of_stock" class="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px] flex items-center justify-center z-[6] select-none pointer-events-none p-4">
        <span class="px-6 py-2 rounded-2xl bg-red-600 text-white font-black text-sm tracking-wider shadow-xl">
          สินค้าหมดชั่วคราว
        </span>
      </div>
    </div>

    <!-- 2. INTERACTIVE 4-THUMBNAIL GALLERY STRIP + NEXT ARROW -->
    <div class="px-4 pt-3.5 pb-3 flex items-center gap-2 bg-white dark:bg-[#111827]">
      <div class="grid grid-cols-4 gap-2 flex-1">
        <button
          v-for="(thumb, tIdx) in visibleThumbnails"
          :key="'thumb-' + tIdx"
          type="button"
          @click="selectThumbnail($event, thumb.originalIndex)"
          class="aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 relative transition-all duration-200 focus:outline-none"
          :class="activeImageIndex === thumb.originalIndex ? 'ring-2 ring-[#0b533b] dark:ring-emerald-400 shadow-sm' : 'border border-slate-200/80 dark:border-slate-700 hover:border-[#0b533b]/60'"
          :aria-label="'ดูรูปสินค้าที่ ' + (tIdx + 1)"
        >
          <img
            v-if="isNearViewport"
            :src="getOptimizedImageUrl(thumb.src, 140)"
            :srcset="`${getOptimizedImageUrl(thumb.src, 140)} 1x, ${getOptimizedImageUrl(thumb.src, 200)} 2x`"
            :alt="(product.title || product.name) + ' รูปที่ ' + (tIdx + 1)"
            loading="lazy"
            decoding="async"
            width="72"
            height="72"
            class="w-full h-full"
            :class="thumb.zoomClass"
            @error="onImgError"
          >
        </button>
      </div>

      <!-- Vertical Pair of Gallery Navigation Buttons (Next > & Prev <) -->
      <div class="flex flex-col items-center justify-center gap-1.5 shrink-0">
        <button
          type="button"
          @click="nextGalleryImage($event)"
          class="w-7 h-7 rounded-full bg-slate-100 hover:bg-[#0b533b] dark:bg-slate-800 dark:hover:bg-emerald-600 text-slate-600 hover:text-white dark:text-slate-300 dark:hover:text-white flex items-center justify-center shrink-0 transition-all duration-200 border border-slate-200/80 dark:border-slate-700 shadow-2xs"
          title="ดูรูปถัดไป"
          aria-label="ดูรูปถัดไป"
        >
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </button>

        <button
          type="button"
          @click="prevGalleryImage($event)"
          class="w-7 h-7 rounded-full bg-slate-100 hover:bg-[#0b533b] dark:bg-slate-800 dark:hover:bg-emerald-600 text-slate-600 hover:text-white dark:text-slate-300 dark:hover:text-white flex items-center justify-center shrink-0 transition-all duration-200 border border-slate-200/80 dark:border-slate-700 shadow-2xs"
          title="ดูรูปก่อนหน้า"
          aria-label="ดูรูปก่อนหน้า"
        >
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 3. DYNAMIC SPECIFICATION BAR (Custom Bottom Bar or Category Defaults) -->
    <div v-if="showBottomBar" class="px-2 py-2.5 bg-white dark:bg-[#111827] border-y border-slate-200/80 dark:border-slate-800 grid divide-x divide-slate-200/80 dark:divide-slate-800" :style="`grid-template-columns: repeat(${bottomBarSpecs.length}, minmax(0, 1fr))`">
      <div
        v-for="(spec, sIdx) in bottomBarSpecs"
        :key="'spec-bar-' + sIdx"
        class="px-1.5 flex items-center justify-center gap-1.5 min-w-0"
      >
        <!-- Circular Outline Icon -->
        <div class="w-7 h-7 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center shrink-0 text-emerald-700 dark:text-emerald-400">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" :d="getCardIconPath(spec.icon)"/>
          </svg>
        </div>

        <!-- 2-Line Spec Label -->
        <div class="min-w-0 leading-[1.18]">
          <p class="text-[9.5px] sm:text-[10px] font-extrabold text-slate-800 dark:text-slate-100 truncate">
            {{ spec.line1 }}
          </p>
          <p v-if="spec.line2" class="text-[8.5px] sm:text-[9px] font-medium text-slate-600 dark:text-slate-300 truncate">
            {{ spec.line2 }}
          </p>
        </div>
      </div>
    </div>

    <!-- 4. BOTTOM FOOTER: LEFT PRICE LOCKUP & RIGHT 2 STACKED BUTTONS -->
    <div class="mt-auto p-3 sm:p-3.5 md:px-5 md:py-4 bg-[#f6f8f7] dark:bg-slate-900/60 flex items-center justify-between gap-2 sm:gap-3">
      
      <!-- Left: Price Stack -->
      <div class="min-w-0 flex flex-col justify-center flex-1">
        <!-- Original Price & Pink Savings Pill -->
        <div v-if="effectiveOriginalPrice > effectivePrice" class="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1 flex-wrap">
          <span class="text-[11px] sm:text-xs md:text-[13px] text-slate-600 dark:text-slate-400 line-through font-bold">
            ฿{{ formatPrice(effectiveOriginalPrice) }}
          </span>
          <span class="px-1.5 sm:px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black bg-[#ffe0e3] text-[#b70020] dark:bg-rose-950/70 dark:text-rose-200 whitespace-nowrap">
            ประหยัด ฿{{ formatPrice(savingsAmount) }}
          </span>
        </div>

        <!-- Large Red Price -->
        <div class="flex items-baseline text-[#dc2626] dark:text-red-400 font-black leading-none my-0.5">
          <span class="text-base sm:text-lg md:text-xl mr-0.5">฿</span>
          <span class="text-xl sm:text-2xl md:text-[30px] tracking-tight">{{ formatPrice(effectivePrice) }}</span>
        </div>

        <!-- Tax Note -->
        <span class="text-[10px] sm:text-[11px] text-slate-600 dark:text-slate-300 font-medium mt-0.5">
          รวมภาษีมูลค่าเพิ่มแล้ว
        </span>
      </div>

      <!-- Right: 2 Vertically Stacked Action Buttons -->
      <div class="flex flex-col gap-1.5 sm:gap-2 w-[128px] xs:w-[138px] sm:w-[166px] shrink-0">
        <!-- Top Button: เพิ่มใส่ตะกร้า → -->
        <button
          type="button"
          @click="handlePrimaryAction($event, product)"
          class="w-full py-2 sm:py-2.5 px-2.5 sm:px-3.5 rounded-xl bg-[#0b533b] hover:bg-[#0d684a] active:bg-[#08422f] text-white font-bold text-[11px] sm:text-xs md:text-[13px] shadow-sm hover:shadow-md flex items-center justify-center gap-1 sm:gap-1.5 transition-all duration-200 group/cartbtn touch-manipulation"
        >
          <svg class="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          <span class="whitespace-nowrap">เพิ่มใส่ตะกร้า</span>
          <svg class="w-3 sm:w-3.5 h-3 sm:h-3.5 text-white transform group-hover/cartbtn:translate-x-0.5 transition-transform shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
          </svg>
        </button>

        <!-- Bottom Button: เปรียบเทียบสินค้า -->
        <button
          type="button"
          @click="toggleCompareItem($event, product)"
          class="w-full py-1.5 sm:py-2 px-2 sm:px-3 rounded-xl font-bold text-[10px] sm:text-[11px] md:text-xs flex items-center justify-center gap-1 sm:gap-1.5 transition-all duration-200 border touch-manipulation"
          :class="compareStore.isInCompare(product.id)
            ? 'bg-emerald-50 dark:bg-emerald-950/40 text-[#0b533b] dark:text-emerald-400 border-[#0b533b]/30 dark:border-emerald-500/40'
            : 'bg-white dark:bg-slate-800 hover:bg-emerald-50/70 dark:hover:bg-emerald-950/30 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-[#0b533b]/30'"
        >
          <svg
            class="w-3 sm:w-3.5 h-3 sm:h-3.5 shrink-0 transition-colors"
            :class="compareStore.isInCompare(product.id) ? 'text-[#0b533b] dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/>
          </svg>
          <span class="whitespace-nowrap">
            {{ compareStore.isInCompare(product.id) ? 'อยู่ในเปรียบเทียบ' : 'เปรียบเทียบสินค้า' }}
          </span>
        </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
.product-card-showcase {
  backface-visibility: hidden;
}

.product-card-showcase:hover {
  will-change: transform, box-shadow;
}

/* ── Interactive Feature Badges (Left Stack) ── */
.feat-stack {
  pointer-events: none;
}

.feat-badge {
  display: flex;
  align-items: center;
  gap: 0;
  height: 32px;
  width: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1),
              0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  pointer-events: auto;
  cursor: pointer;
  transition: width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.25s ease,
              background 0.2s ease;
}

.group:hover .feat-badge {
  width: auto;
  box-shadow: 0 6px 18px rgba(11, 83, 59, 0.2),
              0 2px 6px rgba(0, 0, 0, 0.08);
}

:deep(.dark) .feat-badge,
.dark .feat-badge {
  background: rgba(17, 24, 39, 0.92);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.feat-badge-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0b533b;
  flex-shrink: 0;
}

:deep(.dark) .feat-badge-icon,
.dark .feat-badge-icon {
  color: #34d399;
}

.feat-badge-text {
  font-size: 11px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
  letter-spacing: 0.01em;
  max-width: 0;
  opacity: 0;
  padding-right: 0;
  overflow: hidden;
  transition: max-width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
              opacity 0.2s ease,
              padding-right 0.2s ease;
}

.group:hover .feat-badge-text {
  max-width: 140px;
  opacity: 1;
  padding-right: 10px;
}

:deep(.dark) .feat-badge-text,
.dark .feat-badge-text {
  color: #f1f5f9;
}
</style>
